import { useTitle } from "src/hooks/useTitle";
import { makeStyles } from "@mui/styles";
import { Title } from "src/pages/Messaging/components/Title";
import { ExceptionForm, SkuException } from "./components/StaffPriceExceptionForm";
import { FormProvider, useForm } from "react-hook-form";
import { CreateExceptionModel, createExceptionSchema } from "src/validationSchemas/createExceptionSchema";
import { yupResolver } from "@hookform/resolvers/yup";
import { generateErrorMessages } from "src/utils/formUtils";
import { AlertModal, AlertModalState } from "src/components/Modal";
import { ConfirmModal, ConfirmModalState } from "src/components/Modal";
import { useState } from "react";
import { useHistory } from "react-router-dom";
import { getConsts } from "src/utils/consts";
import { useContext } from "react";
import PageContext from "src/components/Context/PageContext";
import { upsertStaffPriceExceptionsAsync } from "src/infrastructure/http/appApiService";
import { useCountry } from "src/components/Context/CountryContext";
import { ExceptionTypes } from "src/models/staffPriceExceptions/StaffPriceExceptionTypes";
import moment from "moment";

const useStyles = makeStyles((theme) => ({
  title: {
    marginBottom: `${theme.spacing(4)} !important`,
    textAlign: "center",
  },
}));

export const StaffExceptionDetails: React.FC = () => {
  const classes = useStyles();
  const history = useHistory();
  const consts = getConsts();
  const [contextData] = useContext(PageContext);
  const { userProfile } = contextData;
  const { country, timezone } = useCountry();
  const momentInstance = moment.tz.setDefault(timezone);

  const [confirmModalState, setConfirmModalState] = useState<ConfirmModalState>({ show: false });
  const [alertModalState, setAlertModalState] = useState<AlertModalState>({ show: false });

  const emptyException = {
    exceptions: [],
  } as CreateExceptionModel;

  const formMethods = useForm<CreateExceptionModel>({
    resolver: yupResolver<CreateExceptionModel>(createExceptionSchema),
    defaultValues: emptyException,
  });

  useTitle("New Staff Price Exceptions");

  const onFormSubmit = async (data: { exceptions: SkuException[] }) => {
    try {
      const transformedData = data.exceptions.map((item) => ({
        ...item,
        type: item.type,
        details: item.details.join(", "),
        price: item.type == ExceptionTypes.Sku ? Number(item.price) : undefined,
        startDate: item.startDate != null ? moment(item.startDate, "DD/MM/YYYY HH:mm:ss").toISOString() : null,
        endDate: item.endDate != null ? moment(item.endDate, "DD/MM/YYYY HH:mm:ss").toISOString() : null,
      }));

      transformedData.forEach(async (item) => {
        await upsertStaffPriceExceptionsAsync(item, country);
      });
      console.log("here");

      setConfirmModalState({
        title: "Success",
        message: <>Staff price exceptions created successfully</>,
        confirmLabel: "Ok",
        show: true,
        onConfirm: () => {
          history.push(`/${consts.page.staffExceptions}`);
        },
      });
    } catch (error) {
      setAlertModalState({
        show: true,
        title: "Error",
        message: "An error occurred while creating staff price exceptions. Please try again later.",
        type: "error",
      });
    }
  };

  const onFormError = (data: any) => {
    const errorMessages = generateErrorMessages(data);

    setAlertModalState({
      show: true,
      title: "Error",
      message: (
        <>
          <p style={{ marginBottom: 15 }}>
            There are validation errors in the form. Please review and correct them before trying again.
          </p>
          <ul style={{ listStyle: "inside" }}>
            {errorMessages.map((message, index) => (
              <li key={index}>{message}</li>
            ))}
          </ul>
        </>
      ),
      type: "error",
    });
  };

  return (
    <>
      <Title className={classes.title}>Create Staff Price Exception</Title>

      <FormProvider {...formMethods}>
        <ExceptionForm onFormSubmit={onFormSubmit} onFormError={onFormError} />
      </FormProvider>

      <AlertModal
        state={alertModalState}
        onClose={() => {
          setAlertModalState({
            show: false,
          });
        }}
      />

      <ConfirmModal state={confirmModalState} />
    </>
  );
};
